import { useFeatureAccess } from "../../../access-control/hooks/useFeatureAccess";
import { matchesAccessGroupRule } from "../../../access-control/models/access-groups.models";
import { useAuthContext } from "../../../auth/state/AuthContext";
import { hasPendingKeysSecretsRotationAtom, themeAtom } from "../../../shared/atoms";
import { ADMIN_SIDEBAR_ITEMS, AdminPageId } from "../../models/admin.models";
import { adminStyles } from "../../styles/admin.styles";
import AdminSidebarIcon from "./AdminSidebarIcon";
import AdminEmailSidebarUnread from "../email/AdminEmailSidebarUnread";
import { useAtomValue } from "jotai";
import { CSSProperties } from "react";
import { NavLink } from "react-router-dom";
import { twMerge } from "tailwind-merge";

type Props = {
  id: Exclude<AdminPageId, "debug" | "monitoring">;
  isCollapsed: boolean;
};

const PENDING_ROTATION_COLOR = "#BF0002";

const AdminSidebarMainLink = ({ id, isCollapsed }: Props) => {
  const theme = useAtomValue(themeAtom);
  const hasPendingKeysSecretsRotation = useAtomValue(hasPendingKeysSecretsRotationAtom);
  const groups = useAuthContext().state.user?.accessToken?.groups ?? [];
  const { hasAnyFeature, hasFeature } = useFeatureAccess();
  const item = ADMIN_SIDEBAR_ITEMS.find((sidebarItem) => sidebarItem.id === id);

  if (!item) {
    return null;
  }

  const hasRequiredFeature =
    (!item.requiredFeatureKey || hasFeature(item.requiredFeatureKey)) &&
    (!item.requiredFeatureKeys?.length || hasAnyFeature(item.requiredFeatureKeys));
  const hasRequiredGroups = !item.requiredGroups || matchesAccessGroupRule(groups, item.requiredGroups);
  const hasAdditionalAccess = item.additionalAccessRules?.some(
    (rule) => hasAnyFeature(rule.featureKeys) && matchesAccessGroupRule(groups, rule.requiredGroups),
  );

  if ((!hasRequiredFeature || !hasRequiredGroups) && !hasAdditionalAccess) {
    return null;
  }

  const shouldHighlightPendingRotation = item.id === "keysSecrets" && hasPendingKeysSecretsRotation;

  return (
    <NavLink
      to={item.path}
      data-test={item.dataTest}
      title={isCollapsed ? item.label : undefined}
      className={({ isActive }) =>
        twMerge(
          isCollapsed ? adminStyles.sidebar.collapsedLink : adminStyles.sidebar.link,
          item.id === "emails" && "relative",
          theme.home.tailwind.textDefault,
          theme.myVesselsTableRow.row.hoverTwenty,
          isActive && theme.myVesselsTableRow.row.text,
          shouldHighlightPendingRotation && "font-semibold text-[#BF0002]",
        )
      }
      style={({ isActive }) => {
        const style: CSSProperties = {};

        if (isActive) {
          style.background = theme.sideBarComponent.background.activeLink;
          style.color = shouldHighlightPendingRotation
            ? PENDING_ROTATION_COLOR
            : theme.sideBarComponent.iconAndText.active;
        } else if (shouldHighlightPendingRotation) {
          style.color = PENDING_ROTATION_COLOR;
        }

        return Object.keys(style).length > 0 ? style : undefined;
      }}
    >
      <AdminSidebarIcon id={item.id} />
      {!isCollapsed && <span>{item.label}</span>}
      {item.id === "emails" && <AdminEmailSidebarUnread isCollapsed={isCollapsed} />}
    </NavLink>
  );
};

export default AdminSidebarMainLink;
